import moment from "moment";
import { useEffect, useRef, useState } from "react";

/**
 * A useState wrapper which gets and saves a boolean value to localStorage. 
 */
export const useLocalStorageBooleanState = (key: string, defaultValue: boolean = false) => {
  const state = useState(() => {
    const value = localStorage.getItem(key);
    return !value ? defaultValue : value === 'yes';
  });

  useEffect(() => {
    localStorage.setItem(key, state[0] ? 'yes' : 'no');
  }, [state[0]]);

  return state;
}


/**
 * A useState wrapper which gets and saves a boolean value to localStorage. 
 */
export const useLocalStorageStringState = (key: string) => {
  const state = useState<string>(localStorage.getItem(key));

  useEffect(() => {
    if (state[0]) {
      localStorage.setItem(key, state[0]);
    } else {
      localStorage.removeItem(key);
    }
  }, [state[0]]);

  return state;
}

/**
 * A useState wrapper which gets and saves an object value to localStorage. 
 */
export const useLocalStorageObjectState = <T extends object = any>(key: string, defaultValue: T = <any>{}, cls?: { new(params: Partial<T>): T }) => {
  const state = useState<T>(() => {
    const value = localStorage.getItem(key);
    if (value) {
      try {
        const object = JSON.parse(value);
        if (cls) {
          try {
            return new cls(object);
          } catch (error) {
            console.warn("Local storage state %s (%s) could not be instantiated as %s", object, key, cls, error);
          }
        }

        return object;
      } catch (error) {
        console.warn("Local storage state %s (%s) is invalid JSON", value, key, error);
      }
    }

    return defaultValue;
  });

  const json = state[0] ? JSON.stringify(state[0]) : null;
  useEffect(() => {
    if (json) {
      localStorage.setItem(key, json);
    } else {
      localStorage.removeItem(key);
    }
  }, [json]);

  return state;
}

const initialPrevious = {};

/**
 * A useEffect substitute, which fires the effect immediately on the same render cycle,
 * where as a useEffect would fire the effect on the next cycle.
 */
export const useImmediateEffect = (dependency: any, onChange: () => void) => {
  const previous = useRef(initialPrevious); //  Using null or undefined would likely result in a bug if `dependency` is the same value.

  let hasChanged = previous.current === initialPrevious || previous.current !== dependency;

  if (Array.isArray(dependency)) {
    hasChanged = !Array.isArray(previous.current) ||
      dependency.length !== previous.current.length ||
      dependency.findIndex((element, index) => element !== previous.current[index]) !== -1;
  }

  if (hasChanged) {
    onChange();

    previous.current = dependency;
  }
}

type UseForegroundIntervalOptions = {
  delay: number;
  dependencies?: any[];
  immediatelyOnDepsChange?: boolean;
}

/**
 * Calls `work` periodically, unless document.hidden is true.
 * 
 * Once document becomes visible, will do the thing again if `delay` has passed since last exec.
 */
export const useForegroundInterval = (work: () => any, options: UseForegroundIntervalOptions) => {
  const execAt = useRef<Date>(new Date());

  function exec() {
    work();
    execAt.current = new Date();
  }

  useEffect(() => {
    const listener = () => {
      if (!document.hidden && execAt.current.getTime() < Date.now() - options.delay) {
        exec();
      }
    };

    if (options.immediatelyOnDepsChange && !document.hidden) {
      exec();
    }

    const intervalId = setInterval(() => {
      if (execAt.current && execAt.current > moment().subtract(2, 'seconds').toDate()) {
        return;
      }

      if (document.hidden) {
        return;
      }

      exec();
    }, options.delay);

    document.addEventListener('visibilitychange', listener);

    return () => {
      document.removeEventListener('visibilitychange', listener);
      clearInterval(intervalId);
    }
  }, [...options.dependencies ?? [], options.delay]);
}

export const useDocumentHidden = () => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const listener = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', listener);

    return () => { document.removeEventListener('visibilitychange', listener); }
  }, []);

  return hidden;
}